import { useEffect, useState } from "react";
import { Badge, DataTable, EmptyAction, Notice, PageHeader, SlideForm } from "../components/ui";
import { Icon } from "../components/icons";
import { loadRpc } from "../lib/platform";
import { supabase } from "../lib/supabase";

const inputClass = "h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-brand-ink transition duration-200 focus:border-brand-blue";

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 flex items-baseline justify-between gap-3 text-sm font-semibold text-slate-700">
        {label}
        {hint ? <span className="text-xs font-medium text-slate-400">{hint}</span> : null}
      </span>
      {children}
    </label>
  );
}

function useRows(rpc) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    loadRpc(rpc).then((data) => { if (active) setRows(data || []); }).catch((err) => { if (active) setError(err.message); });
    return () => { active = false; };
  }, [rpc]);
  return { rows: rows || [], error, loading: rows === null && !error };
}

const blankSite = { organization_id: "", name: "", code: "", address: "", timezone: "Africa/Nairobi" };

export function Sites() {
  const [rows, setRows] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(blankSite);

  async function load() {
    const [sites, organizations] = await Promise.all([
      supabase.rpc("platform_list_sites"),
      supabase.rpc("platform_list_organizations"),
    ]);
    if (sites.error || organizations.error) {
      setError(sites.error?.message || organizations.error?.message);
      setRows([]);
      return;
    }
    setError("");
    setRows(sites.data || []);
    setOrgs(organizations.data || []);
  }

  useEffect(() => { load(); }, []);

  async function save(event) {
    event.preventDefault();
    setFormError("");
    setBusy(true);
    const { error: createError } = await supabase.rpc("platform_create_site", {
      p_org: form.organization_id,
      p_name: form.name,
      p_code: form.code,
      p_address: form.address || null,
      p_timezone: form.timezone || "Africa/Nairobi",
    });
    setBusy(false);
    if (createError) {
      setFormError(createError.message);
      return;
    }
    setForm(blankSite);
    setOpen(false);
    load();
  }

  return (
    <div>
      <PageHeader title="Sites" description="Add a facility for a client. The client portal can use the site, and cannot create one.">
        <button type="button" className="inline-flex h-10 items-center gap-1.5 rounded-lg bg-brand-orange px-4 text-sm font-semibold text-white hover:bg-[#c4631d]" onClick={() => { setFormError(""); setOpen(true); }}>
          <Icon name="plus" className="h-4 w-4" /> Add site
        </button>
      </PageHeader>
      <SlideForm open={open} title="Add site" description="The site is created for the selected client and starts active." onClose={() => setOpen(false)}>
        <form onSubmit={save} className="space-y-4">
          {formError ? <Notice>{formError}</Notice> : null}
          <Field label="Client">
            <select className={inputClass} required value={form.organization_id} onChange={(event) => setForm({ ...form, organization_id: event.target.value })}>
              <option value="">Select a client</option>
              {orgs.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}
            </select>
          </Field>
          <Field label="Site name">
            <input className={inputClass} required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
          </Field>
          <Field label="Code" hint="Unique for this client">
            <input className={inputClass} required value={form.code} onChange={(event) => setForm({ ...form, code: event.target.value })} />
          </Field>
          <Field label="Address" hint="Optional">
            <input className={inputClass} value={form.address} onChange={(event) => setForm({ ...form, address: event.target.value })} />
          </Field>
          <Field label="Timezone">
            <input className={inputClass} value={form.timezone} onChange={(event) => setForm({ ...form, timezone: event.target.value })} />
          </Field>
          <div className="flex gap-2 pt-2">
            <button className="inline-flex h-11 flex-1 items-center justify-center rounded-lg bg-brand-orange px-4 text-sm font-semibold text-white hover:bg-[#c4631d] disabled:opacity-50" disabled={busy}>{busy ? "Saving" : "Add site"}</button>
            <button type="button" className="h-11 rounded-lg border border-slate-200 px-4 text-sm font-semibold text-slate-600 hover:bg-slate-50" onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </form>
      </SlideForm>
      {error ? <Notice>{error}</Notice> : <DataTable loading={rows === null} empty={{ icon: "sites", title: "No sites yet", body: "Add the first facility for a client.", action: <EmptyAction onClick={() => setOpen(true)}><Icon name="plus" className="h-4 w-4" /> Add site</EmptyAction> }} rows={rows || []} columns={[
        { key: "name", label: "Site" },
        { key: "organization", label: "Client" },
        { key: "code", label: "Code" },
        { key: "timezone", label: "Timezone" },
        { key: "kiosk_count", label: "Kiosks" },
        { key: "status", label: "Status", render: (row) => <Badge>{row.status}</Badge> },
      ]} />}
    </div>
  );
}

export function Kiosks() {
  const [rows, setRows] = useState(null);
  const [sites, setSites] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ organization_id: "", site_id: "", name: "" });

  async function load() {
    const [kiosks, siteRows, organizations] = await Promise.all([
      supabase.rpc("platform_list_kiosks"),
      supabase.rpc("platform_list_sites"),
      supabase.rpc("platform_list_organizations"),
    ]);
    if (kiosks.error || siteRows.error || organizations.error) {
      setError(kiosks.error?.message || siteRows.error?.message || organizations.error?.message);
      setRows([]);
      return;
    }
    setError("");
    setRows(kiosks.data || []);
    setSites(siteRows.data || []);
    setOrgs(organizations.data || []);
  }

  useEffect(() => { load(); }, []);

  const siteChoices = sites.filter((site) => site.organization_id === form.organization_id);

  async function save(event) {
    event.preventDefault();
    setFormError("");
    setBusy(true);
    const { error: createError } = await supabase.rpc("platform_create_kiosk", {
      p_site: form.site_id,
      p_name: form.name,
    });
    setBusy(false);
    if (createError) {
      setFormError(createError.message);
      return;
    }
    setForm({ organization_id: "", site_id: "", name: "" });
    setOpen(false);
    load();
  }

  return (
    <div>
      <PageHeader title="Kiosks" description="Add a kiosk to a client site. It stays offline until the device sends a heartbeat.">
        <button type="button" className="inline-flex h-10 items-center gap-1.5 rounded-lg bg-brand-orange px-4 text-sm font-semibold text-white hover:bg-[#c4631d]" onClick={() => { setFormError(""); setOpen(true); }}>
          <Icon name="plus" className="h-4 w-4" /> Add kiosk
        </button>
      </PageHeader>
      <SlideForm open={open} title="Add kiosk" description="Choose the client and site. Remote restart is not available." onClose={() => setOpen(false)}>
        <form onSubmit={save} className="space-y-4">
          {formError ? <Notice>{formError}</Notice> : null}
          <Field label="Client">
            <select className={inputClass} required value={form.organization_id} onChange={(event) => setForm({ ...form, organization_id: event.target.value, site_id: "" })}>
              <option value="">Select a client</option>
              {orgs.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}
            </select>
          </Field>
          <Field label="Site">
            <select className={inputClass} required value={form.site_id} onChange={(event) => setForm({ ...form, site_id: event.target.value })} disabled={!form.organization_id}>
              <option value="">{form.organization_id ? "Select a site" : "Choose a client first"}</option>
              {siteChoices.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}
            </select>
          </Field>
          {form.organization_id && siteChoices.length === 0 ? <p className="text-sm text-slate-500">This client has no sites yet. Add a site first.</p> : null}
          <Field label="Kiosk name">
            <input className={inputClass} required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
          </Field>
          <div className="flex gap-2 pt-2">
            <button className="inline-flex h-11 flex-1 items-center justify-center rounded-lg bg-brand-orange px-4 text-sm font-semibold text-white hover:bg-[#c4631d] disabled:opacity-50" disabled={busy || !form.site_id}>{busy ? "Saving" : "Add kiosk"}</button>
            <button type="button" className="h-11 rounded-lg border border-slate-200 px-4 text-sm font-semibold text-slate-600 hover:bg-slate-50" onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </form>
      </SlideForm>
      {error ? <Notice>{error}</Notice> : <DataTable loading={rows === null} empty={{ icon: "kiosks", title: "No kiosks yet", body: "Add a kiosk after the client has a site.", action: <EmptyAction onClick={() => setOpen(true)}><Icon name="plus" className="h-4 w-4" /> Add kiosk</EmptyAction> }} rows={rows || []} columns={[
        { key: "name", label: "Kiosk" },
        { key: "organization", label: "Client" },
        { key: "site", label: "Site" },
        { key: "status", label: "Status", render: (row) => <Badge>{row.status}</Badge> },
        { key: "app_version", label: "App version", render: (row) => row.app_version || "Unknown" },
        { key: "last_seen_at", label: "Last seen", render: (row) => row.last_seen_at || "Never" },
      ]} />}
    </div>
  );
}

export function Visits() {
  const { rows, error, loading } = useRows("platform_list_visits");
  return (
    <div>
      <PageHeader title="Visits" description="Latest 200 visits. Identity document numbers are not included." />
      {error ? <Notice>{error}</Notice> : <DataTable loading={loading} empty={{ icon: "visits", title: "No visits yet", body: "Visitor activity will appear here once visits begin.", action: <EmptyAction to="/clients">Open clients</EmptyAction> }} rows={rows} columns={[
        { key: "visitor_name", label: "Visitor" },
        { key: "company", label: "Company" },
        { key: "organization", label: "Client" },
        { key: "site", label: "Site" },
        { key: "host_name", label: "Host" },
        { key: "status", label: "Status", render: (row) => <Badge>{row.status}</Badge> },
        { key: "checked_in_at", label: "Check-in", render: (row) => row.checked_in_at || "—" },
      ]} />}
    </div>
  );
}

export function EmailOps() {
  const { rows, error, loading } = useRows("platform_list_email");
  return (
    <div>
      <PageHeader title="Email" description="Delivery status for queued mail. Message bodies are not shown." />
      {error ? <Notice>{error}</Notice> : <DataTable loading={loading} empty={{ icon: "email", title: "No email yet", body: "Queued messages will appear here after the platform sends mail.", action: <EmptyAction to="/visits">View visits</EmptyAction> }} rows={rows} columns={[
        { key: "template_key", label: "Event" },
        { key: "recipient", label: "Recipient" },
        { key: "organization", label: "Client" },
        { key: "status", label: "Status", render: (row) => <Badge>{row.status}</Badge> },
        { key: "attempt_count", label: "Attempts" },
        { key: "failure_reason", label: "Failure", render: (row) => row.failure_reason || "—" },
      ]} />}
    </div>
  );
}

export function Audit() {
  const { rows, error, loading } = useRows("platform_list_audit");
  return (
    <div>
      <PageHeader title="Audit" description="Latest 200 audit events. These rows cannot be edited from this app." />
      {error ? <Notice>{error}</Notice> : <DataTable loading={loading} empty={{ icon: "audit", title: "No audit events yet", body: "Actions taken in the platform will be listed here.", action: <EmptyAction to="/clients">Open clients</EmptyAction> }} rows={rows} columns={[
        { key: "created_at", label: "When" },
        { key: "actor_email", label: "Actor" },
        { key: "action", label: "Action" },
        { key: "entity_table", label: "Resource" },
        { key: "organization", label: "Client" },
      ]} />}
    </div>
  );
}

export function Staff() {
  const { rows, error, loading } = useRows("platform_list_staff");
  return (
    <div>
      <PageHeader title="Platform staff" description="Globotech operators. Inviting another platform user is done in Supabase Auth, then inserted into platform_admins. Passwords are not shown." />
      {error ? <Notice>{error}</Notice> : <DataTable loading={loading} empty={{ icon: "staff", title: "No platform staff", body: "Globotech operators appear here once they are added.", action: <EmptyAction to="/">Back to dashboard</EmptyAction> }} rows={rows} columns={[
        { key: "full_name", label: "Name", render: (row) => row.full_name || "—" },
        { key: "email", label: "Email" },
        { key: "platform_role", label: "Role", render: (row) => <Badge>{row.platform_role}</Badge> },
        { key: "created_at", label: "Created" },
      ]} />}
    </div>
  );
}
